import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import * as _ from 'lodash-es';
import { Observable, Subject, combineLatest, distinctUntilChanged, filter, startWith } from 'rxjs';

import { LinkDefinition, SeoLinksService } from 'ish-core/services/seo-links/seo-links.service';
import { DomService } from 'ish-core/utils/dom/dom.service';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private internalCanonicalUrl$ = new Subject<string>();
  private internalAlternateLangUrls$ = new Subject<LinkDefinition[]>();
  private destroyRef$ = inject(DestroyRef);
  private url: string;

  canonicalUrl$: Observable<string> = this.internalCanonicalUrl$.asObservable();
  alternateLangUrls$: Observable<LinkDefinition[]> = this.internalAlternateLangUrls$.asObservable().pipe(startWith([]));

  constructor(
    private seoLinkService: SeoLinksService,
    private metaService: Meta,
    private domService: DomService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.url = this.document.URL.replace(/[;?].*/g, '');
    combineLatest([this.canonicalUrl$, this.alternateLangUrls$])
      .pipe(
        filter(([x]) => !!x),
        distinctUntilChanged(_.isEqual),
        takeUntilDestroyed(this.destroyRef$)
      )
      .subscribe(([canonicalUrl, alternateUrls]) => {
        const isCanonicalizedPage = this.checkCanonicalStatus(this.url, canonicalUrl);

        this.setCanonicalLink(canonicalUrl);

        if (isCanonicalizedPage && alternateUrls.length && SSR) {
          this.seoLinkService.setAlternateLinks(alternateUrls);
        }
      });
  }

  setCanonicalUrl(url: string) {
    this.internalCanonicalUrl$.next(url);
  }

  setAlternateUrls(links: LinkDefinition[]) {
    this.internalAlternateLangUrls$.next(links);
  }

  addOrModifyTag(tag: MetaDefinition) {
    if (!this.metaService.updateTag(tag)) {
      this.metaService.addTag(tag);
    }
  }

  private setCanonicalLink(url: string) {
    // the canonical URL of a production system should always be with 'https:'
    // even though the PWA SSR container itself is usually not deployed in an SSL environment so the URLs need manual adaption
    const canonicalUrl = encodeURI(url.replace('http:', 'https:').replace('/home', '').replace(/\/+$/, ''));
    const canonicalLink = this.domService.getOrCreateElement('link[rel="canonical"]', 'link', this.document.head);

    this.domService.setAttribute(canonicalLink, 'rel', 'canonical');
    this.domService.setAttribute(canonicalLink, 'href', canonicalUrl);

    this.addOrModifyTag({ property: 'og:url', content: canonicalUrl });
  }

  private checkCanonicalStatus(pageUrl: string, canonicalUrl: string) {
    const regex = /\/+$/;
    const pageLink = pageUrl.replace(regex, '');
    const canonicalLink = canonicalUrl.replace(regex, '');

    /*

      Temporarily commented the below code for fixing the hreflang not showing on homepage issue

     if (pageLink.endsWith('/home')) {
      return false;
    } */

    return pageLink === canonicalLink;
  }
}
